/* eslint-disable import/first */
// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import Layout from "./components/Layout";
import { AuthProvider } from "./context/AuthContext";

import { Toaster } from "react-hot-toast";

/* ───── Public Pages ──── */
import Home from "./pages/home";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SearchResults from "./pages/SearchResults";
import DownloadTicket from "./pages/DownloadTicket";
import Payment from "./pages/Payment";
import BookingPage from "./pages/BookingPage";
import OperatorSeatPublicView from "./pages/OperatorSeatPublicView";
import OperatorLanding from "./pages/OperatorLanding"; // ✅ NEW
import OperatorBookingPage from "./pages/OperatorBookingPage"; // ✅ NEW

// ✅ now points to src/pages/ConfirmBooking/index.jsx (split mobile + desktop)
import ConfirmBooking from "./pages/ConfirmBooking";

import MyBookings from "./pages/MyBookings";
import Profile from "./pages/Profiles";
import AllOperators from "./pages/AllOperators";
import WhatsNew from "./pages/WhatsNew"; // ✅ ADDED
import PaymentFailed from "./pages/PaymentFailed"; // ✅ NEW
import PaymentReturn from "./pages/PaymentReturn"; // ✅ NEW

// ✅ NEW: Legal pages (PayHere requirement)
import PrivacyPolicy from "./pages/PrivacyPolicy"; // ✅ NEW
import Terms from "./pages/Terms"; // ✅ NEW
import RefundPolicy from "./pages/RefundPolicy"; // ✅ NEW
import Help from "./pages/Help"; // ✅ NEW

// ✅ NEW: Account page (mobile full page)
import Account from "./pages/Account"; // ✅ NEW

/* ───── Admin Pages ──── */
import AdminDashboard from "./pages/AdminDashboard";
import AdminBusList from "./pages/AdminBusList";
import AdminBusView from "./pages/AdminBusView";
import AddBus from "./pages/AddBus";
import EditBus from "./pages/EditBus";
import AdminBookings from "./pages/AdminBookings";
import AdminBookingView from "./pages/AdminBookingView";
import AdminBusAvailability from "./pages/AdminBusAvailability";
import AdminAuditLogs from "./pages/AdminAuditLogs";
import AdminSpecialNotices from "./pages/AdminSpecialNotices";
import AdminRegisterOperator from "./pages/AdminRegisterOperator";
import AdminOperatorList from "./pages/AdminOperatorList";
import AdminOperatorPayments from "./pages/OperatorPaymentsAdmin.jsx"; // ✅ fixed path
import AdminNotices from "./pages/AdminNotices"; // ✅ ADDED
import AdminWhatsNew from "./pages/AdminWhatsNew"; // ✅ ADDED
import AdminHolidays from "./pages/AdminHolidays"; // ✅ NEW
import AdminArrivalsToday from "./pages/AdminArrivalsToday"; // ✅ NEW
import AdminLogin from "./pages/AdminLogin"; // ✅ NEW
import AdminSettlements from "./pages/AdminSettlements"; // ✅ NEW

/* ───── Operator Pages ──── */
import OperatorDashboard from "./pages/OperatorDashboard";
import OperatorProfile from "./pages/OperatorProfile";
import OperatorBusList from "./pages/operator/OperatorBusList";
import OperatorBusSeatView from "./pages/operator/OperatorBusSeatView";

/* ───── Route Guards ──── */
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";

/* ───── UI ──── */
import MobileBottomNav from "./components/MobileBottomNav";

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* ───── PUBLIC ROUTES ───── */}
        <Route path="/" element={<Home />} />
        <Route path="/demo/:operatorSlug" element={<OperatorLanding />} /> {/* ✅ NEW */}
        <Route path="/operator-booking/:operatorSlug" element={<OperatorBookingPage />} /> {/* ✅ NEW */}
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/search-results" element={<SearchResults />} />
        <Route path="/download-ticket" element={<DownloadTicket />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/payment-return" element={<PaymentReturn />} /> {/* ✅ NEW */}
        <Route path="/payment-failed" element={<PaymentFailed />} /> {/* ✅ NEW */}
        <Route path="/operators" element={<AllOperators />} />
        <Route path="/operators/:id" element={<OperatorProfile />} />
        <Route path="/whats-new" element={<WhatsNew />} /> {/* ✅ ADDED */}
        <Route path="/admin/login" element={<AdminLogin />} /> {/* ✅ NEW */}
        <Route path="/operator-seat-view" element={<OperatorSeatPublicView />} />

        {/* ✅ NEW: Mobile Account page (redBus style) */}
        <Route path="/account" element={<Account />} /> {/* ✅ NEW */}

        {/* ✅ NEW: Legal pages (PayHere) */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/help" element={<Help />} /> {/* ✅ NEW */}

        {/* ───── USER PROTECTED ROUTES ───── */}
        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute>
              <MyBookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/book/:busId"
          element={
            <ProtectedRoute>
              <BookingPage />
            </ProtectedRoute>
          }
        />
        <Route path="/confirm-booking" element={<ConfirmBooking />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* ───── ADMIN ROUTES ───── */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/buses"
          element={
            <AdminRoute>
              <AdminBusList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/buses/:busId"
          element={
            <AdminRoute>
              <AdminBusView />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/add-bus"
          element={
            <AdminRoute>
              <AddBus />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/edit-bus/:busId"
          element={
            <AdminRoute>
              <EditBus />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <AdminRoute>
              <AdminBookings />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bookings/:id"
          element={
            <AdminRoute>
              <AdminBookingView />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/availability"
          element={
            <AdminRoute>
              <AdminBusAvailability />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <AdminRoute>
              <AdminAuditLogs />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/special-notices"
          element={
            <AdminRoute>
              <AdminSpecialNotices />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/operators"
          element={
            <AdminRoute>
              <AdminOperatorList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/register-operator"
          element={
            <AdminRoute>
              <AdminRegisterOperator />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/operator-payments"
          element={
            <AdminRoute>
              <AdminOperatorPayments />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/settlements"
          element={
            <AdminRoute>
              <AdminSettlements />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/notices"
          element={
            <AdminRoute>
              <AdminNotices />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/whats-new"
          element={
            <AdminRoute>
              <AdminWhatsNew />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/holidays"
          element={
            <AdminRoute>
              <AdminHolidays />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/arrivals-today"
          element={
            <AdminRoute>
              <AdminArrivalsToday />
            </AdminRoute>
          }
        />

        {/* ───── OPERATOR ROUTES ──── */}
        <Route
          path="/operator/dashboard"
          element={
            <ProtectedRoute>
              <OperatorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operator/profile"
          element={
            <ProtectedRoute>
              <OperatorProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operator/buses"
          element={
            <ProtectedRoute>
              <OperatorBusList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operator/bus/:busId"
          element={
            <ProtectedRoute>
              <OperatorBusSeatView />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

// ───── App Component ─────
const App = () => (
  <AuthProvider>
    <Router>
      <Layout>
        <Toaster position="top-right" />
        <AnimatedRoutes />
        <MobileBottomNav />
      </Layout>
    </Router>
  </AuthProvider>
);

export default App;
